import { CreateExportedReport, ExportedReportItem } from '@futrli-sdk/report_scheduling';
import { LiveReportDetail, LiveReportRequest } from '@futrli-sdk/predict';

import {
  createNewLiveReport,
  deleteLiveReport,
  duplicateLiveReport,
  fetchLiveReportById,
  patchLiveReport
} from '../../predict/src/core/api/reporting/report-builder';
import { CARD_TYPES, LayoutData, ReportMappingData } from '../../predict/src/report-builder/types';
import { getReferencedDatasources, getUnpublishedFormulas } from '../../predict/src/report-builder/utils-data';
import {
  duplicateReportTemplate,
  deleteReportTemplate,
  patchReportTemplate,
  fetchReportTemplateById,
  fetchAvailableTemplateFormulaData,
  bulkPublishTemplateFormulas
} from '../../predict/src/core/api/reporting/report-templates';
import { createExportedReport } from '../../predict/src/core/api/reporting/report-document-export';

import { createMutation } from '../utils/mutations';
import { liveReportsQueryKey } from '../queries/liveReports';
import { reportTemplatesQueryKey } from '../queries/reportTemplates';
import { queryClient } from '../core/queryClient';

// ── Duplicate Report ──

export interface DuplicateReportInput {
  reportId: string;
  reportName: string;
  inaccessibleForecastIds?: string[];
}

interface DuplicateReportData {
  [key: string]: unknown;
  data_reference_mapping?: ReportMappingData;
  pageLayoutData?: LayoutData[];
}

const duplicateReportWithoutForecasts = async (
  workspaceId: string,
  organisationId: string,
  input: DuplicateReportInput,
  isConsolidation: boolean
): Promise<LiveReportDetail> => {
  const report = await fetchLiveReportById(workspaceId, organisationId, input.reportId, isConsolidation);
  const inaccessibleForecastIds = new Set(input.inaccessibleForecastIds ?? []);
  const reportData = ((report.data as unknown) as DuplicateReportData) ?? {};
  const pageLayoutData = (reportData.pageLayoutData ?? []).map(item => {
    if (item.type !== CARD_TYPES.WIDGET || !item[CARD_TYPES.WIDGET]) {
      return item;
    }

    return {
      ...item,
      [CARD_TYPES.WIDGET]: {
        ...item[CARD_TYPES.WIDGET],
        selectedDatasets: (item[CARD_TYPES.WIDGET]?.selectedDatasets ?? []).filter(
          forecastId => !inaccessibleForecastIds.has(forecastId)
        )
      }
    };
  });

  const dataReferenceMapping = reportData.data_reference_mapping;
  const sanitizedReportData: DuplicateReportData = {
    ...reportData,
    pageLayoutData,
    ...(dataReferenceMapping
      ? {
          data_reference_mapping: {
            ...dataReferenceMapping,
            dataset_references: Object.fromEntries(
              Object.entries(dataReferenceMapping.dataset_references ?? {}).filter(
                ([forecastId, mapping]) =>
                  !inaccessibleForecastIds.has(forecastId) && !inaccessibleForecastIds.has(mapping?.id)
              )
            )
          }
        }
      : {})
  };

  return createNewLiveReport(
    workspaceId,
    organisationId,
    {
      name: input.reportName,
      locale: report.locale,
      orientation: report.orientation,
      page_count: report.page_count,
      data: sanitizedReportData,
      referenced_datasources: getReferencedDatasources(pageLayoutData, organisationId, isConsolidation)
    },
    isConsolidation
  );
};

export const duplicateReportMutation = (workspaceId: string, organisationId: string, isConsolidation: boolean) =>
  createMutation<DuplicateReportInput, LiveReportDetail>({
    mutationFn: async (input: DuplicateReportInput) =>
      input.inaccessibleForecastIds?.length
        ? duplicateReportWithoutForecasts(workspaceId, organisationId, input, isConsolidation)
        : duplicateLiveReport(workspaceId, organisationId, input.reportId, input.reportName, isConsolidation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: liveReportsQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Publish / Unpublish Report ──

export interface PublishReportInput {
  reportId: string;
  status: 'live' | 'draft';
}

export const publishReportMutation = (workspaceId: string, organisationId: string, isConsolidation: boolean) =>
  createMutation<PublishReportInput, LiveReportDetail>({
    mutationFn: async (input: PublishReportInput) =>
      patchLiveReport(
        workspaceId,
        organisationId,
        input.reportId,
        { status: input.status } as Partial<LiveReportRequest>,
        isConsolidation
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: liveReportsQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Delete Report ──

export interface DeleteReportInput {
  reportId: string;
}

export const deleteReportMutation = (workspaceId: string, organisationId: string, isConsolidation: boolean) =>
  createMutation<DeleteReportInput, void>({
    mutationFn: async (input: DeleteReportInput) =>
      deleteLiveReport(workspaceId, organisationId, input.reportId, isConsolidation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: liveReportsQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Delete Template ──

export interface DeleteTemplateInput {
  templateId: string;
}

export const deleteTemplateMutation = (workspaceId: string, organisationId: string, isConsolidation: boolean) =>
  createMutation<DeleteTemplateInput, void>({
    mutationFn: async (input: DeleteTemplateInput) =>
      deleteReportTemplate(workspaceId, organisationId, input.templateId, isConsolidation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reportTemplatesQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Export Report to PDF ──

export interface ExportReportToPdfInput {
  data: CreateExportedReport;
}

export const exportReportToPdfMutation = (workspaceId: string, organisationId: string, isConsolidation: boolean) =>
  createMutation<ExportReportToPdfInput, ExportedReportItem>({
    mutationFn: async (input: ExportReportToPdfInput) =>
      createExportedReport(workspaceId, organisationId, input.data, isConsolidation)
  });

// ── Lock / Unlock Report ──

export interface LockReportInput {
  reportId: string;
  is_locked: boolean;
}

export const lockReportMutation = (workspaceId: string, organisationId: string, isConsolidation: boolean) =>
  createMutation<LockReportInput, LiveReportDetail>({
    mutationFn: async (input: LockReportInput) =>
      patchLiveReport(
        workspaceId,
        organisationId,
        input.reportId,
        { is_locked: input.is_locked } as Partial<LiveReportRequest>,
        isConsolidation
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: liveReportsQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Publish / Unpublish Template ──

export interface PublishTemplateInput {
  templateId: string;
  status: 'published' | 'draft';
  /** Also publish the draft template formulas used by the template (Futrli templates) */
  publishFormulas?: boolean;
}

type PatchTemplateResult = Awaited<ReturnType<typeof patchReportTemplate>>;

export const publishTemplateMutation = (workspaceId: string, organisationId: string, isConsolidation: boolean) =>
  createMutation<PublishTemplateInput, PatchTemplateResult>({
    mutationFn: async (input: PublishTemplateInput) => {
      if (input.publishFormulas && input.status === 'published') {
        const [template, formulas] = await Promise.all([
          fetchReportTemplateById(workspaceId, organisationId, input.templateId, isConsolidation),
          fetchAvailableTemplateFormulaData(workspaceId, organisationId, isConsolidation)
        ]);
        await bulkPublishTemplateFormulas(
          workspaceId,
          organisationId,
          getUnpublishedFormulas(template, formulas).map(formula => formula.id),
          isConsolidation
        );
      }

      return patchReportTemplate(
        workspaceId,
        organisationId,
        input.templateId,
        { status: input.status },
        isConsolidation
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reportTemplatesQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });

// ── Duplicate Template ──

export interface DuplicateTemplateInput {
  templateId: string;
  templateName: string;
}

type DuplicateTemplateResult = Awaited<ReturnType<typeof duplicateReportTemplate>>;

export const duplicateTemplateMutation = (workspaceId: string, organisationId: string, isConsolidation: boolean) =>
  createMutation<DuplicateTemplateInput, DuplicateTemplateResult>({
    mutationFn: async (input: DuplicateTemplateInput) =>
      duplicateReportTemplate(workspaceId, organisationId, input.templateId, input.templateName, isConsolidation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reportTemplatesQueryKey(organisationId, workspaceId, isConsolidation)
      });
    }
  });
